import React, { useEffect, useState } from "react";
import {
  Form,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";

import { fetchWrapper } from "@helpers/fetchwrapper";
import LoadBlock from "@areas/shared/components/personal/loads/LoadBlock";

import styles from "@areas/shared/components/personal/modals/styles/customModal.module.css";

/** Ожидаемые эндпоинты бэкенда: GET/POST с idCompany и полями ниже (camelCase или PascalCase в ответе). */
const SURVEY_GET = (idCompany) =>
  `/v1/personal/getCompanyMentorshipSurvey?idCompany=${idCompany}`;
const SURVEY_SAVE = `/v1/personal/saveCompanyMentorshipSurvey`;

const AUDIENCE_OPTIONS = [
  { value: "a", label: "Студенты-практиканты" },
  {
    value: "b",
    label:
      "Молодые специалисты – выпускники колледжей, вузов",
  },
  { value: "c", label: "Новые сотрудники предприятия" },
  {
    value: "d",
    label:
      "Специалисты предприятия с других отделов/подразделений",
  },
];

const ANCHORING_OPTIONS = [
  { value: "a", label: "Приказ" },
  { value: "b", label: "Положение о наставничестве" },
  {
    value: "c",
    label: "Иные нормативно-правовые акты (укажите – какие)",
  },
];

const MENTOR_TRAINING_OPTIONS = [
  { value: "a", label: "Нет" },
  {
    value: "b",
    label:
      "Да – индивидуальное от других коллег-наставников предприятия или от непосредственного руководителя",
  },
  { value: "c", label: "Да - внутреннее, в своих учебных центрах" },
  { value: "d", label: "Иное (укажите – какое)" },
];

const pick = (d, camel, pascal) => {
  if (!d || typeof d !== "object") return "";
  const v = d[camel] ?? d[pascal];
  return v != null ? String(v) : "";
};

const initialValues = (survey) => {
  const d = survey || {};
  return {
    goal: pick(d, "goal", "Goal"),
    tasks: pick(d, "tasks", "Tasks"),
    audience: pick(d, "audience", "Audience"),
    anchoring: pick(d, "anchoring", "Anchoring"),
    anchoringOther: pick(d, "anchoringOther", "AnchoringOther"),
    implementation: pick(d, "implementation", "Implementation"),
    mentorTraining: pick(d, "mentorTraining", "MentorTraining"),
    mentorTrainingOther: pick(d, "mentorTrainingOther", "MentorTrainingOther"),
    effectivenessCriteria: pick(
      d,
      "effectivenessCriteria",
      "EffectivenessCriteria"
    ),
  };
};

const validationSchema = () =>
  Yup.object({
    goal: Yup.string(),
    tasks: Yup.string(),
    audience: Yup.string(),
    anchoring: Yup.string(),
    anchoringOther: Yup.string().when("anchoring", {
      is: "other_acts",
      then: (schema) =>
        schema.required("Укажите, какие нормативно-правовые акты"),
      otherwise: (schema) => schema,
    }),
    implementation: Yup.string(),
    mentorTraining: Yup.string(),
    mentorTrainingOther: Yup.string().when("mentorTraining", {
      is: "other",
      then: (schema) => schema.required("Опишите иное обучение"),
      otherwise: (schema) => schema,
    }),
    effectivenessCriteria: Yup.string(),
  });

const saveSurvey = (values, idCompany, onFinally) => {
  const data = new FormData();
  data.append("idCompany", idCompany);
  data.append("goal", values.goal || "");
  data.append("tasks", values.tasks || "");
  data.append("audience", values.audience || "");
  data.append("anchoring", values.anchoring || "");
  data.append("anchoringOther", values.anchoringOther || "");
  data.append("implementation", values.implementation || "");
  data.append("mentorTraining", values.mentorTraining || "");
  data.append("mentorTrainingOther", values.mentorTrainingOther || "");
  data.append("effectivenessCriteria", values.effectivenessCriteria || "");

  fetchWrapper
    .post(SURVEY_SAVE, data, false)
    .then(() => {})
    .catch((error) => toast.error(error))
    .finally(onFinally);
};

function MainInfoCard({ currentCompany }) {
  const [survey, setSurvey] = useState(null);
  const [load, setLoad] = useState(false);

  const idCompany = currentCompany && currentCompany.IdCompany;

  useEffect(() => {
    if (!idCompany) return;
    setLoad(true);
    fetchWrapper
      .get(SURVEY_GET(idCompany))
      .then((res) => setSurvey(res && typeof res === "object" ? res : {}))
      .catch(() => setSurvey({}))
      .finally(() => setLoad(false));
  }, [idCompany]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialValues(survey),
    validationSchema: validationSchema(),
    onSubmit: (values) => {
      if (!idCompany) return;
      setLoad(true);
      saveSurvey(values, idCompany, () => setLoad(false));
    },
  });

  return (
    <Card className="mb-3">
      <CardHeader>
        <h5 className="mb-0">Основная информация о наставничестве на предприятии</h5>
      </CardHeader>
      <CardBody className="position-relative">
        <LoadBlock visible={load}>
          <ClipLoader />
        </LoadBlock>
        <Form
          className="tablelist-form"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row className="g-3">
            <Col lg={12}>
              <Label htmlFor="mentorshipSurvey-goal" className={styles.label}>
                1.1. Цель наставничества на предприятии
              </Label>
              <Input
                id="mentorshipSurvey-goal"
                name="goal"
                type="textarea"
                rows={3}
                className={`form-control ${styles.input}`}
                value={validation.values.goal}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.goal && validation.errors.goal)
                }
              />
              {validation.touched.goal && validation.errors.goal ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.goal}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <Label htmlFor="mentorshipSurvey-tasks" className={styles.label}>
                1.2. Задачи наставничества
              </Label>
              <Input
                id="mentorshipSurvey-tasks"
                name="tasks"
                type="textarea"
                rows={3}
                className={`form-control ${styles.input}`}
                value={validation.values.tasks}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.tasks && validation.errors.tasks)
                }
              />
              {validation.touched.tasks && validation.errors.tasks ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.tasks}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <Label htmlFor="mentorshipSurvey-audience" className={styles.label}>
                1.3. Для кого организовано наставничество
              </Label>
              <Input
                id="mentorshipSurvey-audience"
                name="audience"
                type="select"
                className={`form-control ${styles.input}`}
                value={validation.values.audience}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.audience && validation.errors.audience)
                }
              >
                <option value="">Выберите вариант</option>
                {AUDIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
              {validation.touched.audience && validation.errors.audience ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.audience}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <Label htmlFor="mentorshipSurvey-anchoring" className={styles.label}>
                1.4. Чем закреплено наставничество на предприятии
              </Label>
              <Input
                id="mentorshipSurvey-anchoring"
                name="anchoring"
                type="select"
                className={`form-control ${styles.input}`}
                value={validation.values.anchoring}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.anchoring &&
                    validation.errors.anchoring
                  )
                }
              >
                <option value="">Выберите вариант</option>
                {ANCHORING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
              {validation.touched.anchoring && validation.errors.anchoring ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.anchoring}
                </FormFeedback>
              ) : null}
            </Col>

            {validation.values.anchoring === "c" ? (
              <Col lg={12}>
                <Label
                  htmlFor="mentorshipSurvey-anchoringOther"
                  className={styles.label}
                >
                  Уточнение (иные НПА)
                </Label>
                <Input
                  id="mentorshipSurvey-anchoringOther"
                  name="anchoringOther"
                  type="textarea"
                  rows={2}
                  className={`form-control ${styles.input}`}
                  value={validation.values.anchoringOther}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation.touched.anchoringOther &&
                      validation.errors.anchoringOther
                    )
                  }
                />
                {validation.touched.anchoringOther &&
                validation.errors.anchoringOther ? (
                  <FormFeedback type="invalid" className={styles.invalid}>
                    {validation.errors.anchoringOther}
                  </FormFeedback>
                ) : null}
              </Col>
            ) : null}

            <Col lg={12}>
              <Label
                htmlFor="mentorshipSurvey-implementation"
                className={styles.label}
              >
                1.5. Реализация наставничества (опишите систему наставничества на
                Вашем предприятии, в организации)
              </Label>
              <Input
                id="mentorshipSurvey-implementation"
                name="implementation"
                type="textarea"
                rows={4}
                className={`form-control ${styles.input}`}
                value={validation.values.implementation}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.implementation &&
                    validation.errors.implementation
                  )
                }
              />
              {validation.touched.implementation &&
              validation.errors.implementation ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.implementation}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <Label
                htmlFor="mentorshipSurvey-mentorTraining"
                className={styles.label}
              >
                1.6. Проходят ли наставники обучение
              </Label>
              <Input
                id="mentorshipSurvey-mentorTraining"
                name="mentorTraining"
                type="select"
                className={`form-control ${styles.input}`}
                value={validation.values.mentorTraining}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.mentorTraining &&
                    validation.errors.mentorTraining
                  )
                }
              >
                <option value="">Выберите вариант</option>
                {MENTOR_TRAINING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
              {validation.touched.mentorTraining &&
              validation.errors.mentorTraining ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.mentorTraining}
                </FormFeedback>
              ) : null}
            </Col>

            {validation.values.mentorTraining === "d" ? (
              <Col lg={12}>
                <Label
                  htmlFor="mentorshipSurvey-mentorTrainingOther"
                  className={styles.label}
                >
                  Уточнение (иное обучение)
                </Label>
                <Input
                  id="mentorshipSurvey-mentorTrainingOther"
                  name="mentorTrainingOther"
                  type="textarea"
                  rows={2}
                  className={`form-control ${styles.input}`}
                  value={validation.values.mentorTrainingOther}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation.touched.mentorTrainingOther &&
                      validation.errors.mentorTrainingOther
                    )
                  }
                />
                {validation.touched.mentorTrainingOther &&
                validation.errors.mentorTrainingOther ? (
                  <FormFeedback type="invalid" className={styles.invalid}>
                    {validation.errors.mentorTrainingOther}
                  </FormFeedback>
                ) : null}
              </Col>
            ) : null}

            <Col lg={12}>
              <Label
                htmlFor="mentorshipSurvey-effectivenessCriteria"
                className={styles.label}
              >
                1.7. Есть ли критерии результативности наставничества на
                предприятии (опишите, если предусмотрены)
              </Label>
              <Input
                id="mentorshipSurvey-effectivenessCriteria"
                name="effectivenessCriteria"
                type="textarea"
                rows={3}
                className={`form-control ${styles.input}`}
                value={validation.values.effectivenessCriteria}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.effectivenessCriteria &&
                    validation.errors.effectivenessCriteria
                  )
                }
              />
              {validation.touched.effectivenessCriteria &&
              validation.errors.effectivenessCriteria ? (
                <FormFeedback type="invalid" className={styles.invalid}>
                  {validation.errors.effectivenessCriteria}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <div className="hstack gap-2 justify-content-end mt-2">
                <button type="submit" className="btn btn-success" id="mentorship-survey-save">
                  Сохранить
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
}

export default MainInfoCard;
